import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useProduct } from '@/hooks/useProduct';
import {
  getArticles,
  getVideos,
  getAudioFiles,
  getAssessments,
  syncCalBookings,
  getUpcomingBookings,
  cancelBooking,
  getCurrentUser,
} from '@/lib/api';

// Types
export interface Article {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  content?: string;
  created_at?: string;
}

export interface AudioFile {
  id: string;
  title: string;
  description: string;
  category: string;
  audio_url: string;
  duration: number;
  created_at: string;
}

export interface VideoFile {
  id: string;
  title: string;
  description: string;
  category: string;
  video_url: string;
  duration: number;
  created_at: string;
  is_vr: boolean;
  vr_projection: string;
  vr_stereo_mode: string;
}

export interface Assessment {
  id: string;
  title: string;
  description: string;
  created_at: string;
}

export interface Booking {
  id: string;
  meeting_date: string;
  attendee_name: string;
  attendee_email: string;
  meeting_url: string;
  status: string;
}

// Query keys factory for consistent cache management
export const dashboardKeys = {
  all: ['dashboard'] as const,
  articles: (language: string, productId: string) => 
    [...dashboardKeys.all, 'articles', language, productId] as const,
  videos: (language: string, productId: string) => 
    [...dashboardKeys.all, 'videos', language, productId] as const,
  audio: (language: string, productId: string) => 
    [...dashboardKeys.all, 'audio', language, productId] as const,
  assessments: (language: string, productId: string) => 
    [...dashboardKeys.all, 'assessments', language, productId] as const,
  bookings: (userId: string) => 
    [...dashboardKeys.all, 'bookings', userId] as const,
};

// Hook for articles
export function useArticles(enabled: boolean = true) {
  const { i18n } = useTranslation();
  const { productId } = useProduct();
  const language = i18n.language;

  return useQuery({
    queryKey: dashboardKeys.articles(language, productId),
    queryFn: async (): Promise<Article[]> => {
      const result = await getArticles(language, productId);
      if (!result.success) throw new Error('Failed to fetch articles');
      return result.data.map(a => ({
        id: a.id,
        title: a.title,
        excerpt: a.excerpt || '',
        category: a.category,
      }));
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook for videos
export function useVideos(enabled: boolean = true) {
  const { i18n } = useTranslation();
  const { productId } = useProduct();
  const language = i18n.language;

  return useQuery({
    queryKey: dashboardKeys.videos(language, productId),
    queryFn: async (): Promise<VideoFile[]> => {
      const result = await getVideos(language, productId);
      if (!result.success) throw new Error('Failed to fetch videos');
      return result.data.map(v => ({
        id: v.id,
        title: v.title,
        description: v.description || '',
        category: v.category,
        video_url: v.video_url || '',
        duration: v.duration || 0,
        created_at: v.created_at || '',
        is_vr: v.is_vr || false,
        vr_projection: v.vr_projection || 'equirectangular',
        vr_stereo_mode: v.vr_stereo_mode || 'mono',
      }));
    },
    enabled,
    staleTime: 1000 * 60 * 5,
  });
}

// Hook for audio files
export function useAudioFiles(enabled: boolean = true) {
  const { i18n } = useTranslation();
  const { productId } = useProduct();
  const language = i18n.language;

  return useQuery({
    queryKey: dashboardKeys.audio(language, productId),
    queryFn: async (): Promise<AudioFile[]> => {
      const result = await getAudioFiles(language, productId);
      if (!result.success) throw new Error('Failed to fetch audio files');
      return result.data.map(a => ({
        id: a.id,
        title: a.title,
        description: a.description || '',
        category: a.category,
        audio_url: a.audio_url || '',
        duration: a.duration || 0,
        created_at: a.created_at || '',
      }));
    },
    enabled,
    staleTime: 1000 * 60 * 5,
  });
}

// Hook for assessments
export function useAssessments(enabled: boolean = true) {
  const { i18n } = useTranslation();
  const { productId } = useProduct();
  const language = i18n.language;

  return useQuery({
    queryKey: dashboardKeys.assessments(language, productId),
    queryFn: async (): Promise<Assessment[]> => {
      const result = await getAssessments(language, productId);
      if (!result.success) throw new Error('Failed to fetch assessments');
      return result.data.map(a => ({
        id: a.id,
        title: a.title,
        description: a.description || '',
        created_at: a.created_at || '',
      }));
    },
    enabled,
    staleTime: 1000 * 60 * 5,
  });
}

// Hook for bookings with sync
export function useBookings(userId: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: dashboardKeys.bookings(userId || ''),
    queryFn: async (): Promise<Booking[]> => {
      if (!userId) return [];

      // Fire-and-forget: sync bookings in background (don't block UI)
      syncCalBookings().catch((syncErr) => {
        console.error('Background booking sync failed:', syncErr);
      });

      // Immediately fetch from database without waiting for sync
      const userResult = await getCurrentUser();
      if (!userResult.success) return [];

      const bookingsResult = await getUpcomingBookings(userResult.data.id);
      if (bookingsResult.success) {
        return bookingsResult.data as Booking[];
      }
      return [];
    },
    enabled: enabled && !!userId,
    staleTime: 1000 * 60 * 2, // 2 minutes for bookings
  });
}

// Mutation for canceling bookings
export function useCancelBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookingId: string) => {
      const result = await cancelBooking(bookingId);
      if (!result.success) {
        const errorMsg = 'error' in result ? result.error.message : t('common:errors.failedToCancelBooking', 'Failed to cancel booking');
        throw new Error(errorMsg);
      }
      return bookingId;
    },
    onSuccess: (cancelledId) => {
      // Optimistically update the bookings cache
      queryClient.setQueriesData<Booking[]>(
        { queryKey: dashboardKeys.all },
        (old) => old?.filter(b => b.id !== cancelledId)
      );
    },
  });
}

// Combined hook for all dashboard content
export function useDashboardContent(enabled: boolean = true) {
  const articles = useArticles(enabled);
  const videos = useVideos(enabled);
  const audio = useAudioFiles(enabled);
  const assessments = useAssessments(enabled);

  return {
    articles: articles.data ?? [],
    videos: videos.data ?? [],
    audio: audio.data ?? [],
    assessments: assessments.data ?? [],
    isLoading: articles.isLoading || videos.isLoading || audio.isLoading || assessments.isLoading,
    isError: articles.isError || videos.isError || audio.isError || assessments.isError,
  };
}
